"use client";

import Image from "next/image";
import { useI18n } from "@/lib/i18n/LanguageProvider";
import { withUtm } from "@/lib/utm";
import { track } from "@/lib/tracking";

const Footer = () => {
  const { t } = useI18n();
  const utm = (url: string) =>
    withUtm(url, { medium: "link", campaign: "footer" });

  return (
    <footer className="bg-secondary/30 py-12 border-t border-border">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Image
                src="/assets/flyer-teacher-logo.png"
                alt="Teado Logo"
                width={64}
                height={64}
                className="rounded-lg flex-shrink-0"
              />
              <p className="text-sm font-medium text-foreground/90 leading-snug">
                <span className="font-bold text-primary">Teado</span> — Teachers and doers change the world!
              </p>
            </div>
            <div className="space-y-2 text-sm text-foreground/80">
              <p>
                <strong>{t.footer.company}</strong>
              </p>
              <p>
                {t.footer.address.map((line, i) => (
                  <span key={i}>
                    {line}
                    {i < t.footer.address.length - 1 && <br />}
                  </span>
                ))}
              </p>
              <p>
                <a
                  href="mailto:admin@flyer.us"
                  onClick={() => track("contact_click", { channel: "email" })}
                  className="hover:text-primary transition-colors"
                >
                  {t.footer.email}
                </a>
              </p>
            </div>
          </div>

          {/* Links */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-foreground">
              {t.footer.aboutHeading}
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href={utm("https://flyer.us/flyer-construction-team/")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground/80 hover:text-primary transition-colors"
                >
                  {t.footer.aboutLinks.team}
                </a>
              </li>
              <li>
                <a
                  href={utm("https://flyer.us/cambridge-tests/")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground/80 hover:text-primary transition-colors"
                >
                  {t.footer.aboutLinks.blog}
                </a>
              </li>
            </ul>

            <h3 className="font-bold text-lg mb-4 mt-6 text-foreground">
              {t.footer.productsHeading}
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href={utm("https://exam.flyer.us/en")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground/80 hover:text-primary transition-colors"
                >
                  {t.footer.products.virtualTest}
                </a>
              </li>
              <li>
                <a
                  href={utm(t.links.footerTeado)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground/80 hover:text-primary transition-colors"
                >
                  {t.footer.products.teado}
                </a>
              </li>
              <li>
                <a
                  href={utm("https://study.flyer.us/?selectedLevel=starters")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground/80 hover:text-primary transition-colors"
                >
                  {t.footer.products.bingo}
                </a>
              </li>
              <li>
                <a
                  href={utm("https://ieltsguru.ai/")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground/80 hover:text-primary transition-colors"
                >
                  {t.footer.products.ielts}
                </a>
              </li>
            </ul>
          </div>

          {/* App Download */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-foreground">
              {t.footer.appHeading}
            </h3>
            <div className="space-y-3">
              <a
                href="https://apps.apple.com/vn/app/teacher-assistant-ai/id6449689901"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  track("cta_click", {
                    cta_label: "App Store",
                    cta_location: "footer_app_download",
                    target_url: "https://apps.apple.com/vn/app/teacher-assistant-ai/id6449689901",
                  })
                }
                className="block"
              >
                <div className="bg-background border border-border rounded-lg p-3 hover:border-primary transition-colors">
                  <p className="font-semibold text-foreground">
                    {t.footer.iosLabel}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t.footer.iosSub}
                  </p>
                </div>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.flyer.partner&hl=en"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  track("cta_click", {
                    cta_label: "Google Play",
                    cta_location: "footer_app_download",
                    target_url: "https://play.google.com/store/apps/details?id=com.flyer.partner&hl=en",
                  })
                }
                className="block"
              >
                <div className="bg-background border border-border rounded-lg p-3 hover:border-primary transition-colors">
                  <p className="font-semibold text-foreground">
                    {t.footer.androidLabel}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t.footer.androidSub}
                  </p>
                </div>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-border text-center text-sm text-muted-foreground">
          <p>{t.footer.rights}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
